import { useEffect, useCallback } from 'react'
import { supabase, type Database } from '@/lib/supabaseClient'
import { useEnergyStore } from '@/stores/useEnergyStore'
import { BASE_MATRIX_DATA } from '@/data/planPresets'
import type { PlanRates, UserConfig } from '@/types'

type PlanRow = Database['public']['Tables']['plans']['Row']
type ConfigRow = Database['public']['Tables']['saved_configs']['Row']
type ProfileRow = Database['public']['Tables']['user_profiles']['Row']
type HouseholdRow = Database['public']['Tables']['households']['Row'] & {
  household_members: { role: string }[]
}

export const useSync = () => {
  const userId = useEnergyStore((s) => s.userId)
  const isAuthenticated = useEnergyStore((s) => s.isAuthenticated)
  const isOnline = useEnergyStore((s) => s.isOnline)
  const pendingSync = useEnergyStore((s) => s.pendingSync)
  const dirtyPlanIds = useEnergyStore((s) => s.dirtyPlanIds)
  const dirtyConfigIds = useEnergyStore((s) => s.dirtyConfigIds)
  
  const setPlans = useEnergyStore((s) => s.setPlans)
  const setPendingSync = useEnergyStore((s) => s.setPendingSync)
  const setLastSyncedAt = useEnergyStore((s) => s.setLastSyncedAt)
  const setHouseholds = useEnergyStore((s) => s.setHouseholds)
  const setSubscriptionTier = useCallback((tier: 'free' | 'pro' | 'family') => useEnergyStore.setState({ subscriptionTier: tier }), [])
  const setConfig = useCallback((config: UserConfig) => useEnergyStore.setState({ config }), [])
  const setDarkMode = useCallback((darkMode: boolean) => useEnergyStore.setState({ darkMode }), [])

  const fetchFromCloud = useCallback(async () => {
    if (!userId) return

    // 5s Timeout for initial fetch
    const timeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Sync timeout')), 5000)
    )

    try {
      const fetchPromise = Promise.all([
        supabase.from('plans').select('*'),
        supabase.from('saved_configs').select('*').eq('user_id', userId),
        supabase.from('user_profiles').select('preferences, subscription_tier').eq('id', userId).single(),
        supabase.from('households').select(`
          *,
          household_members!inner(role)
        `).eq('household_members.user_id', userId)
      ])

      const [plansRes, configsRes, profileRes, householdsRes] = await Promise.race([fetchPromise, timeout]) as [
        { data: PlanRow[] | null; error: unknown },
        { data: ConfigRow[] | null; error: unknown },
        { data: ProfileRow | null; error: unknown },
        { data: HouseholdRow[] | null; error: unknown }
      ]

      if (profileRes.data) {
        if (profileRes.data.subscription_tier) {
          setSubscriptionTier(profileRes.data.subscription_tier)
        }
        if (profileRes.data.preferences) {
          const prefs = profileRes.data.preferences as Record<string, unknown>
          let hasCloudPrefs = false
          if (prefs.config) {
            setConfig(prefs.config)
            hasCloudPrefs = true
          }
          if (prefs.darkMode !== undefined) {
            setDarkMode(prefs.darkMode)
            document.documentElement.classList.toggle('dark', prefs.darkMode)
            hasCloudPrefs = true
          }
          if (prefs.portfolios) {
            useEnergyStore.setState({ portfolios: prefs.portfolios })
            hasCloudPrefs = true
          }
          if (prefs.userLevel) {
            useEnergyStore.setState({ userLevel: prefs.userLevel })
            hasCloudPrefs = true
          }
          if (prefs.activePortfolioId) {
            useEnergyStore.setState({ activePortfolioId: prefs.activePortfolioId })
            hasCloudPrefs = true
          }
          if (prefs.activeConfigId) {
            useEnergyStore.setState({ activeConfigId: prefs.activeConfigId })
            hasCloudPrefs = true
          }
          if (prefs.selectedPlan) {
            useEnergyStore.setState({ selectedPlan: prefs.selectedPlan })
            hasCloudPrefs = true
          }
          if (prefs.selectedConfig) {
            useEnergyStore.setState({ selectedConfig: prefs.selectedConfig })
            hasCloudPrefs = true
          }

          if (!hasCloudPrefs) {
            // Cloud has no prefs: check backup key for restored preferences
            try {
              const backup = JSON.parse(localStorage.getItem('energy-app-restore') || '{}')
              if (backup.config) setConfig(backup.config)
              if (backup.portfolios) useEnergyStore.setState({ portfolios: backup.portfolios })
              if (backup.userLevel) useEnergyStore.setState({ userLevel: backup.userLevel })
              if (backup.activePortfolioId) useEnergyStore.setState({ activePortfolioId: backup.activePortfolioId })
              if (backup.activeConfigId) useEnergyStore.setState({ activeConfigId: backup.activeConfigId })
              if (backup.selectedPlan) useEnergyStore.setState({ selectedPlan: backup.selectedPlan })
              if (backup.selectedConfig) useEnergyStore.setState({ selectedConfig: backup.selectedConfig })
            } catch { /* noop */ }
            useEnergyStore.setState({ pendingSync: true })
          }
        }
      }

      if (plansRes.data) {
        const cloudPlans: PlanRates[] = plansRes.data.map((row: PlanRow) => ({
          ...(row.data as PlanRates),
          id: row.id || (row.data as Record<string, unknown>).id as string,
          userId: row.user_id,
          isShared: row.is_shared,
          householdId: row.household_id
        }))
        
        const deletedPresetIds = useEnergyStore.getState().config.deletedPresetIds || []
        const activePresets = BASE_MATRIX_DATA.filter(p => !deletedPresetIds.includes(p.id!))
        
        // Check if cloud actually has data — if not, preserve local state
        if (cloudPlans.length > 0) {
          // Cloud has plans: merge presets with cloud data (overwrites local)
          setPlans([...activePresets, ...cloudPlans])
        } else {
          // Cloud is empty: check backup key (survives logout state reset), then fall back to local
          let restoredPlans: PlanRates[] = []
          try {
            const backup = JSON.parse(localStorage.getItem('energy-app-restore') || '{}')
            if (backup.plans?.length) {
              restoredPlans = backup.plans
            }
          } catch { /* noop */ }
          if (restoredPlans.length === 0) {
            // No backup — keep current state and mark for push
            const currentPlans = useEnergyStore.getState().plans
            const customPlans = currentPlans.filter(p => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(p.id))
            if (customPlans.length > 0) {
              const dirtyIds = new Set(customPlans.map(p => p.id))
              useEnergyStore.setState({ dirtyPlanIds: dirtyIds, pendingSync: true })
            }
          } else {
            // Backup found — use it directly (it already contains presets + custom plans)
            const deletedPresetIds = useEnergyStore.getState().config.deletedPresetIds || []
            const filtered = restoredPlans.filter(p => !deletedPresetIds.includes(p.id!))
            const customIds = filtered.filter(p => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(p.id))
            setPlans(filtered)
            if (customIds.length > 0) {
              useEnergyStore.setState({
                dirtyPlanIds: new Set(customIds.map(p => p.id)),
                pendingSync: true,
              })
            }
          }
        }
      }

      if (householdsRes.data) {
        const cloudHouseholds = householdsRes.data.map((h: HouseholdRow) => ({
          id: h.id,
          name: h.name,
          owner_id: h.owner_id,
          invite_code: h.invite_code,
          role: h.household_members[0]?.role
        }))
        setHouseholds(cloudHouseholds)
      }

      if (configsRes.data) {
        type ConfigData = { id: string; name: string; config: Record<string, unknown>; isShared: boolean; householdId: string | null }
        const cloudConfigs: ConfigData[] = configsRes.data.map((row: ConfigRow) => ({
          id: row.id,
          name: row.name,
          config: row.config,
          isShared: row.is_shared,
          householdId: row.household_id
        }))

        if (cloudConfigs.length > 0) {
          // Cloud has configs: overwrite local state
          useEnergyStore.setState({ savedConfigs: cloudConfigs })
        } else {
          // Cloud is empty: check backup key, then fall back to local
          let restoredConfigs: Array<Record<string, unknown>> = []
          try {
            const backup = JSON.parse(localStorage.getItem('energy-app-restore') || '{}')
            if (backup.savedConfigs?.length) {
              restoredConfigs = backup.savedConfigs
            }
          } catch { /* noop */ }
          if (restoredConfigs.length === 0) {
            const currentConfigs = useEnergyStore.getState().savedConfigs
            const customConfigs = currentConfigs.filter(c => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c.id))
            if (customConfigs.length > 0) {
              const dirtyIds = new Set(customConfigs.map(c => c.id))
              useEnergyStore.setState({ dirtyConfigIds: dirtyIds, pendingSync: true })
            }
          } else {
            const customConfigs = restoredConfigs.filter((c) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c.id as string))
            useEnergyStore.setState({ savedConfigs: restoredConfigs })
            if (customConfigs.length > 0) {
              useEnergyStore.setState({
                dirtyConfigIds: new Set(customConfigs.map(c => c.id)),
                pendingSync: true,
              })
            }
          }
        }
      }

      setLastSyncedAt(new Date().toISOString())
      setPendingSync(false)
    } catch (err) {
      console.error('Fetch from cloud failed or timed out:', err)
      setPendingSync(false)
    }
  }, [userId, setPlans, setLastSyncedAt, setPendingSync, setHouseholds, setSubscriptionTier, setConfig, setDarkMode])

  const pushToCloud = useCallback(async () => {
    if (!userId || !isOnline) return

    const { plans, config, darkMode, dirtyPlanIds, clearDirtyPlans, savedConfigs, portfolios, dirtyConfigIds, clearDirtyConfigs, userLevel, activePortfolioId, activeConfigId, selectedPlan, selectedConfig } = useEnergyStore.getState()
    if (dirtyPlanIds.size === 0 && dirtyConfigIds.size === 0 && !pendingSync) return

    try {
      // Atomic Sync for Plans
      const dirtyIds = Array.from(dirtyPlanIds)
      const toDelete = dirtyIds
        .filter(id => id.startsWith('delete:'))
        .map(id => id.split(':')[1]!)
        .filter(id => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id))
      const toUpsert = dirtyIds
        .filter(id => !id.startsWith('delete:'))
        .map(id => plans.find(p => p.id === id))
        .filter(Boolean)
        .filter(plan => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(plan!.id))
        .map(plan => ({
          id: plan!.id,
          user_id: userId,
          name: plan!.plan,
          data: plan,
          is_shared: plan!.isShared || false,
          household_id: plan!.householdId || null,
          updated_at: new Date().toISOString(),
        }))

      // Single bulk delete for Plans
      if (toDelete.length > 0) {
        const { error } = await supabase.from('plans').delete().in('id', toDelete).eq('user_id', userId)
        if (error) throw error
      }

      // Single bulk upsert for Plans
      if (toUpsert.length > 0) {
        const { error } = await supabase.from('plans').upsert(toUpsert, { onConflict: 'id' })
        if (error) throw error
      }
      clearDirtyPlans()

      // Atomic Sync for Saved Configs
      const dirtyConfigIdsArr = Array.from(dirtyConfigIds)
      const configsToDelete = dirtyConfigIdsArr
        .filter(id => id.startsWith('delete:'))
        .map(id => id.split(':')[1]!)
        .filter(id => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id))
      const configsToUpsert = dirtyConfigIdsArr
        .filter(id => !id.startsWith('delete:'))
        .map(id => savedConfigs.find(c => c.id === id))
        .filter(Boolean)
        .filter(c => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c!.id))
        .map(c => ({
          id: c!.id,
          user_id: userId,
          name: c!.name,
          config: c!.config,
          is_shared: c!.isShared || false,
          household_id: c!.householdId || null,
        }))

      // Single bulk delete for Saved Configs
      if (configsToDelete.length > 0) {
        const { error } = await supabase.from('saved_configs').delete().in('id', configsToDelete).eq('user_id', userId)
        if (error) throw error
      }

      // Single bulk upsert for Saved Configs
      if (configsToUpsert.length > 0) {
        const { error } = await supabase.from('saved_configs').upsert(configsToUpsert, { onConflict: 'id' })
        if (error) throw error
      }
      clearDirtyConfigs()

      // Profile Preferences (Config, darkMode, portfolios, userLevel, activePortfolioId, activeConfigId, selectedPlan, selectedConfig)
      const { error: profileError } = await supabase.from('user_profiles').update({
        preferences: { config, darkMode, portfolios, userLevel, activePortfolioId, activeConfigId, selectedPlan, selectedConfig, lastSyncedAt: new Date().toISOString() }
      }).eq('id', userId)
      if (profileError) throw profileError

      setLastSyncedAt(new Date().toISOString())
      setPendingSync(false)
      // Data synced to cloud — clear the restore backup key
      try { localStorage.removeItem('energy-app-restore') } catch { /* noop */ }
    } catch (err) {
      console.error('Push to cloud failed:', err)
      setPendingSync(false)
    }
  }, [userId, isOnline, setLastSyncedAt, setPendingSync, pendingSync])

  // Initial fetch on login
  useEffect(() => {
    if (isAuthenticated) {
      fetchFromCloud()
    }
  }, [isAuthenticated, fetchFromCloud])

  // Push on changes
  useEffect(() => {
    if (isAuthenticated && (pendingSync || dirtyPlanIds.size > 0 || dirtyConfigIds.size > 0) && isOnline) {
      const timer = setTimeout(() => {
        pushToCloud()
      }, 2000) // Debounce sync
      return () => clearTimeout(timer)
    }
  }, [isAuthenticated, pendingSync, isOnline, pushToCloud, dirtyPlanIds, dirtyConfigIds])

  return { fetchFromCloud, pushToCloud }
}
